// Mijn overzicht — Employee mobile Home. Answers the three questions an
// employee arrives with, in this exact priority order:
//   1. "When do I work next?"          → Next shift hero (Feb 2026)
//   2. "How much leave do I have?"     → Leave balance card
//   3. "What am I earning this month?" → Expected pay card
// Birthdays are shown as a low-emphasis colleague strip below.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import { PlaneTakeoff, Wallet, PartyPopper, Clock, MapPin, CalendarDays } from "lucide-react";
import { NavLink } from "react-router-dom";

export default function MyOverview() {
  const { t, lang } = useI18n();
  const locale = { nl: "nl-NL", en: "en-GB", ar: "ar" }[lang] || "nl-NL";
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [today, setToday] = useState(null);
  const [nextShift, setNextShift] = useState(null);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    (async () => {
      const [lb, ps, td, ns, ll] = await Promise.all([
        api.get("/my/leave-balance").catch(() => ({ data: null })),
        api.get("/my/payroll-summary?period=month").catch(() => ({ data: null })),
        api.get("/today").catch(() => ({ data: null })),
        api.get("/my/next-shift").catch(() => ({ data: null })),
        api.get("/locations").catch(() => ({ data: [] })),
      ]);
      setLeaveBalance(lb.data);
      setPayrollSummary(ps.data);
      setToday(td.data);
      setNextShift(ns.data || null);
      setLocations(ll.data || []);
    })();
  }, []);

  const lb = leaveBalance || { allocated: 25, remaining: 0, approved_days: 0, pending_days: 0, used_pct: 0 };
  const remaining = lb.remaining ?? 0;
  const lbAccent = remaining <= 3 ? "hsl(0 75% 55%)" : remaining <= 8 ? "hsl(38 92% 50%)" : "hsl(160 65% 42%)";

  const ps = payrollSummary || { total_amount: 0, total_hours: 0, currency: "EUR", has_rate: false, shifts_count: 0 };
  const money = new Intl.NumberFormat(lang === "en" ? "en-GB" : "nl-NL", {
    style: "currency", currency: ps.currency || "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(ps.total_amount || 0);

  const bdays = [
    ...((today?.birthdays_today || []).map((b) => ({ ...b, when: t("today"), highlight: true }))),
    ...(today?.birthdays_upcoming || []),
  ].slice(0, 8);

  return (
    <div data-testid="page-my-overview" className="max-w-3xl">
      <PageHeader
        eyebrow={t("my_overview_eyebrow") || "Mijn cijfers"}
        title={t("my_overview_title") || "Mijn overzicht"}
        subtitle={t("my_overview_subtitle") || "Je verlof, je verwachte uitbetaling en jarige collega's — allemaal op één plek."}
      />

      {/* Next shift hero — the #1 question an employee has when opening the app. */}
      {nextShift && (() => {
        const nsDate = new Date(nextShift.date + "T00:00:00");
        const loc = locations.find((l) => l._id === nextShift.location_id);
        const nsColor = loc?.color || "#0033A0";
        const todayISO = new Date().toISOString().slice(0, 10);
        const isToday = nextShift.date === todayISO;
        return (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="mb-4"
            data-testid="mo-next-shift"
          >
            <NavLink
              to="/my-roster"
              className="block card p-5 border-l-4 hover:shadow-[var(--shadow-md)] transition-shadow"
              style={{ borderLeftColor: nsColor, background: "hsl(var(--primary-soft) / 0.35)" }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-11 h-11 rounded-2xl grid place-items-center shrink-0"
                  style={{ background: `${nsColor}22`, color: nsColor }}
                >
                  <CalendarDays size={18} strokeWidth={1.9} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--primary))]" data-testid="mo-next-shift-eyebrow">
                    {isToday ? (t("next_shift_today_eyebrow") || t("today")) : (t("next_shift_eyebrow") || "Volgende dienst")}
                  </div>
                  <div className="font-display text-[18px] md:text-[20px] font-bold tracking-tight text-[hsl(var(--text-strong))] mt-0.5 leading-tight capitalize truncate" data-testid="mo-next-shift-date">
                    {nsDate.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}
                  </div>
                  <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                    <div className="inline-flex items-center gap-1.5 text-[14px] font-semibold tabular-nums text-[hsl(var(--text))]" data-testid="mo-next-shift-time">
                      <Clock size={13} strokeWidth={2} className="text-[hsl(var(--text-muted))]" />
                      {(nextShift.start_time || "").slice(0, 5)}–{(nextShift.end_time || "").slice(0, 5)}
                    </div>
                    {loc?.name && (
                      <div className="inline-flex items-center gap-1.5 text-[12px] text-[hsl(var(--text-muted))]" data-testid="mo-next-shift-location">
                        <MapPin size={12} strokeWidth={2} />
                        {loc.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </NavLink>
          </motion.div>
        );
      })()}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {/* Leave balance card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="card p-6"
          data-testid="mo-leave-balance"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl grid place-items-center" style={{ background: `${lbAccent}18`, color: lbAccent }}>
              <PlaneTakeoff size={18} strokeWidth={1.9} />
            </div>
            <div>
              <div className="eyebrow !text-[10px]">{t("year")} {lb.year || new Date().getFullYear()}</div>
              <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))]">{t("w_my_leave_balance")}</div>
            </div>
          </div>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="font-display text-[42px] font-bold tabular-nums leading-none text-[hsl(var(--text-strong))]">{remaining}</div>
            <div className="text-[14px] text-[hsl(var(--text-muted))]">/ {lb.allocated} {t("days")}</div>
          </div>
          <div className="h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden mb-3">
            <div className="h-full transition-all" style={{ width: `${Math.min(100, lb.used_pct || 0)}%`, background: lbAccent }} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded-lg bg-[hsl(var(--muted))]">
              <div className="text-[11px] text-[hsl(var(--text-muted))]">{t("approved") || "Goedgekeurd"}</div>
              <div className="font-display font-bold tabular-nums text-[15px]">{lb.approved_days}</div>
            </div>
            <div className="p-2 rounded-lg bg-[hsl(var(--muted))]">
              <div className="text-[11px] text-[hsl(var(--text-muted))]">{t("pending") || "Aanvraag"}</div>
              <div className="font-display font-bold tabular-nums text-[15px]">{lb.pending_days}</div>
            </div>
            <div className="p-2 rounded-lg bg-[hsl(var(--primary-soft))]">
              <div className="text-[11px] text-[hsl(var(--primary))] font-semibold">{t("remaining") || "Rest"}</div>
              <div className="font-display font-bold tabular-nums text-[15px] text-[hsl(var(--primary))]">{remaining}</div>
            </div>
          </div>
        </motion.div>

        {/* Expected pay card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05 }}
          className="card p-6"
          data-testid="mo-expected-pay"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl bg-[hsl(217_91%_92%)] text-[hsl(217_91%_45%)] grid place-items-center">
              <Wallet size={18} strokeWidth={1.9} />
            </div>
            <div>
              <div className="eyebrow !text-[10px]">{t("payroll_this_month")}</div>
              <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))]">{t("w_my_expected_pay")}</div>
            </div>
          </div>
          {ps.has_rate ? (
            <>
              <div className="font-display text-[32px] font-bold tabular-nums leading-none text-[hsl(var(--text-strong))] mb-3">
                {money}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded-lg bg-[hsl(var(--muted))]">
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">{t("payroll_total_hours")}</div>
                  <div className="font-display font-bold tabular-nums text-[15px]">{(ps.total_hours || 0).toFixed(1)}u</div>
                </div>
                <div className="p-2 rounded-lg bg-[hsl(var(--muted))]">
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">{t("shifts") || "Diensten"}</div>
                  <div className="font-display font-bold tabular-nums text-[15px]">{ps.shifts_count}</div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-6 text-center text-[13px] text-[hsl(var(--text-muted))] italic">
              {t("payroll_no_rate")}
            </div>
          )}
        </motion.div>
      </div>

      {/* Birthdays card */}
      {bdays.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}
          className="card p-6"
          data-testid="mo-birthdays"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl bg-[hsl(45_95%_92%)] text-[hsl(38_92%_45%)] grid place-items-center">
              <PartyPopper size={18} strokeWidth={1.9} />
            </div>
            <div>
              <div className="eyebrow !text-[10px]">{bdays.length} {bdays.length === 1 ? (t("colleague") || "collega") : (t("colleagues") || "collega's")}</div>
              <div className="font-display text-[16px] font-bold text-[hsl(var(--text-strong))]">{t("w_birthdays_this_week")}</div>
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto -mx-1 px-1">
            {bdays.map((b) => (
              <div key={b._id} className="shrink-0 flex flex-col items-center gap-2 min-w-[76px]">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(217_91%_45%)] text-white text-[13px] font-bold grid place-items-center shadow-[var(--shadow-sm)]">
                    {(b.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  {b.highlight && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[hsl(45_95%_60%)] text-[11px] grid place-items-center border-2 border-[hsl(var(--surface))]">🎂</span>
                  )}
                </div>
                <div className="text-[11px] font-semibold text-[hsl(var(--text-strong))] truncate max-w-[76px] text-center">{b.name?.split(" ")[0]}</div>
                <div className={`text-[9px] uppercase font-black tracking-wider ${b.highlight ? "text-[hsl(38_92%_45%)]" : "text-[hsl(var(--text-muted))]"}`}>
                  {b.when || (b.mmdd || "").replace("-", "/")}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
